import { type UploadStatus, type VoiceRecording } from '@/models/voiceRecording';

import { STALE_UPLOAD_TIMEOUT_MS } from './constants';
import { type VoiceRecordingRepository } from './types';
import { resolveWebKeyValueStore, type WebKeyValueStore } from './webKeyValueStore';

const STORAGE_KEY = 'digima.voice_recordings';

const byRecordedAtDesc = (a: VoiceRecording, b: VoiceRecording) =>
  b.recordedAt.localeCompare(a.recordedAt);

const byCreatedAtAsc = (a: VoiceRecording, b: VoiceRecording) =>
  a.createdAt.localeCompare(b.createdAt);

const isSameAccount = (recording: VoiceRecording, accountId: string) =>
  String(recording.accountId) === String(accountId);

const distinctMeetingIds = (recordings: VoiceRecording[], status: UploadStatus): string[] => {
  const meetingIds = recordings
    .filter((recording) => recording.meetingId !== null && recording.uploadStatus === status)
    .map((recording) => recording.meetingId as string);

  return [...new Set(meetingIds)];
};

const createWebVoiceRecordingRepository = (store: WebKeyValueStore): VoiceRecordingRepository => {
  const readAll = (): VoiceRecording[] => {
    const raw = store.getItem(STORAGE_KEY);
    if (raw === null) {
      return [];
    }

    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as VoiceRecording[]) : [];
    } catch {
      return [];
    }
  };

  const writeAll = (recordings: VoiceRecording[]) => {
    store.setItem(STORAGE_KEY, JSON.stringify(recordings));
  };

  const updateById = (id: string, patch: Partial<VoiceRecording>) => {
    const updatedAt = new Date().toISOString();
    writeAll(
      readAll().map((recording) =>
        recording.id === id ? { ...recording, ...patch, updatedAt } : recording,
      ),
    );
  };

  return {
    initialize: async () => {
      const updatedAt = new Date().toISOString();
      writeAll(
        readAll().map((recording) => {
          if (recording.uploadStatus !== 'uploading') {
            return recording;
          }
          const uploadStatus: UploadStatus = recording.meetingId !== null ? 'pending' : 'failed';
          return { ...recording, uploadStatus, updatedAt };
        }),
      );
    },

    insert: async (recording) => {
      const recordings = readAll();
      if (recordings.some((existing) => existing.id === recording.id)) {
        throw new Error(`Voice recording ${recording.id} already exists`);
      }
      writeAll([...recordings, recording]);
    },

    findByContactId: async (contactId, accountId) =>
      readAll()
        .filter(
          (recording) => recording.contactId === contactId && isSameAccount(recording, accountId),
        )
        .sort(byRecordedAtDesc),

    findById: async (id) => readAll().find((recording) => recording.id === id) ?? null,

    findAllByAccountId: async (accountId) =>
      readAll()
        .filter((recording) => isSameAccount(recording, accountId))
        .sort(byRecordedAtDesc),

    findPendingUploads: async () => {
      const staleBefore = new Date(Date.now() - STALE_UPLOAD_TIMEOUT_MS).toISOString();

      return readAll()
        .filter((recording) => {
          if (recording.uploadStatus === 'pending' || recording.uploadStatus === 'failed') {
            return true;
          }
          return recording.uploadStatus === 'uploading' && recording.updatedAt < staleBefore;
        })
        .sort(byCreatedAtAsc);
    },

    findMeetingIdsWithPendingUploads: async () => distinctMeetingIds(readAll(), 'pending'),

    findMeetingIdsWithFailedUploads: async () => distinctMeetingIds(readAll(), 'failed'),

    updateUploadStatus: async (id, status) => {
      updateById(id, { uploadStatus: status });
    },

    updateMeetingId: async (id, meetingId) => {
      updateById(id, { meetingId });
    },

    deleteById: async (id) => {
      writeAll(readAll().filter((recording) => recording.id !== id));
    },
  };
};

const voiceRecordingRepository = createWebVoiceRecordingRepository(resolveWebKeyValueStore());

export { STALE_UPLOAD_TIMEOUT_MS, createWebVoiceRecordingRepository, voiceRecordingRepository };
